import { useTransportInfiniteQuery } from "@/transport"
import { keepPreviousData } from "@tanstack/react-query"

import { getMetadataFilterValidationError } from "@/lib/metadataFilters"
import type { TransportOperationRequest } from "@/types_api/backend/transport/operations"

export type ConversationKeysetRequest =
  TransportOperationRequest<"queryUploadedConversationKeyset">

const UPLOADED_CONVERSATION_KEYSET_OPERATION =
  "queryUploadedConversationKeyset" as const
const UPLOADED_CONVERSATION_KEYSET_STALE_TIME = 5 * 60 * 1000

export function useConversationsKeysetInfiniteTransport({
  enabled = true,
  request,
}: {
  enabled?: boolean
  request: ConversationKeysetRequest
}) {
  const validationError = getMetadataFilterValidationError(
    request.filters?.metadata_filters,
  )
  const query = useTransportInfiniteQuery({
    operation: UPLOADED_CONVERSATION_KEYSET_OPERATION,
    request: validationError ? null : request,
    getNextRequest: (lastPage, _allPages, previousRequest) => {
      if (!lastPage.next_cursor || !previousRequest) {
        return null
      }

      return {
        ...previousRequest,
        cursor: lastPage.next_cursor,
      }
    },
    options: {
      enabled: enabled && !validationError,
      abortOnUnmount: true,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: UPLOADED_CONVERSATION_KEYSET_STALE_TIME,
      placeholderData: keepPreviousData,
    },
  })
  return {
    ...query,
    data: validationError ? undefined : query.data,
    error: validationError ? new Error(validationError) : query.error,
    isError: Boolean(validationError) || query.isError,
    isLoading: !validationError && query.isLoading,
    hasNextPage: !validationError && query.hasNextPage,
    isFetched: Boolean(validationError) || query.isFetched,
  }
}
